import PropTypes from 'prop-types'
import React from 'react'
import { connect } from 'react-redux'
import classNames from 'classnames'
import moment from 'moment'
import { Tooltip } from 'react-wot-bootstrap'

import { openBattleRooms } from 'state/actions/unit_create'
import { JOURNAL_TABS, openJournal } from 'state/actions/journal'
import { openChallenges, requestNextChallengeInfo } from 'state/actions/challenges'
import { playButtonClickSound, playClickSound, playHoverSound } from 'state/actions/sounds'
import { openSchedule } from 'state/actions/schedule'
import { INDUSTRIAL_RESOURCE_MULTIPLIER_TOOLTIP_TEXT } from 'components/IndustrialResourceCoefTooltip/constants'

import Timer from 'components/Timer/Timer'

import { getCustomDateTime, getTodayTime, getTomorrowTime, isTodayDay, isTomorrowDay } from 'helpers/datetime'
import { isFeatureAvailable } from 'helpers/accounts'
import { scheduleOpenThrottler } from 'helpers/schedule'
import { CHALLENGES, LOG, SHOP_STOREFRONT } from 'urls'
import { MAX_SET_TIMEOUT_VALUE, FEATURE_TYPES } from 'constants'

import { BattlesButton, Item, ScheduleButton, Storage } from './components'

import {
    CHALLENGES_OFF,
    CHALLENGES_TIMER,
    CHALLENGES_TITLE,
    CHALLENGES_TOOLTIP_ENDS,
    CHALLENGES_TOOLTIP_FOOTER,
    CHALLENGES_TOOLTIP_ID,
    CHALLENGES_TOOLTIP_NEXT_CUSTOM_DATE,
    CHALLENGES_TOOLTIP_NEXT_STAGE_CUSTOM_DATE,
    CHALLENGES_TOOLTIP_NEXT_STAGE_TODAY,
    CHALLENGES_TOOLTIP_NEXT_STAGE_TOMORROW,
    CHALLENGES_TOOLTIP_NEXT_TODAY,
    CHALLENGES_TOOLTIP_NEXT_TOMORROW,
    CHALLENGES_TOOLTIP_OFF,
    CHALLENGES_TOOLTIP_STAGE_ENDS,
    INDUSTRIAL_RESOURCE_BUYIN_BUTTON,
    JOURNAL_TITLE,
    JOURNAL_TOOLTIP,
    JOURNAL_TOOLTIP_ID,
    OPEN_BATTLE_LIST_BUTTON_CAPTION,
    OPEN_BATTLE_LIST_BUTTON_TOOLTIP_ID,
    OPEN_BATTLE_LIST_BUTTON_TOOLTIP_MESSAGE,
    OPEN_BATTLE_LIST_BUTTON_TOOLTIP_UNITS_AVAILABLE,
} from './constants'

import styles from './ClanPanel.scss'
import Button from 'react-wot-bootstrap/lib/components/Button/Button'
import { push } from 'react-router-redux'
import IndustrialBuyinTooltip from './components/IndustrialBuyinTooltip/IndustrialBuyinTooltip'
import { GA_ACTIONS, GA_LABEL, trackEvent } from 'googleAnalytic'

export class ClanPanel extends React.Component {
    static contextTypes = {
        router: PropTypes.object,
    }

    static propTypes = {
        challenge: PropTypes.shape({
            start_datetime: PropTypes.number,
            end_datetime: PropTypes.number,
            primetimes_count: PropTypes.number,
            primetime_index: PropTypes.number,
        }),
        clanId: PropTypes.number,
        clanIndustrialResource: PropTypes.number,
        clanUnitsNumber: PropTypes.number,
        isClanJournalEnabled: PropTypes.bool.isRequired,
        isScheduleEnabled: PropTypes.bool.isRequired,
        industrialResourceBuyinAvailable: PropTypes.bool.isRequired,
        journalSelectedTab: PropTypes.string,
        location: PropTypes.string.isRequired,
        showScheduleBubble: PropTypes.bool,
        industrialResourceMultiplier: PropTypes.number,

        openShop: PropTypes.func.isRequired,
        openBattleRooms: PropTypes.func.isRequired,
        openChallenges: PropTypes.func.isRequired,
        openJournal: PropTypes.func.isRequired,
        openSchedule: PropTypes.func.isRequired,
        playButtonClickSound: PropTypes.func.isRequired,
        playClickSound: PropTypes.func.isRequired,
        playHoverSound: PropTypes.func.isRequired,
        requestNextChallengeInfo: PropTypes.func.isRequired,
    }

    componentWillUnmount() {
        this.clearTimeouts()
    }

    setUpdateTimer() {
        if (this.timer) clearTimeout(this.timer)

        const now = moment()
        const nowValue = now.valueOf()
        const start = this.props.challenge.start_datetime

        if (start && nowValue > start) return

        const updateTimeout = (start && nowValue < start) ?
            start - nowValue :
            now.endOf('hour').valueOf() - nowValue

        this.timer = setTimeout(this.handleChallengesStatusChange, Math.min(updateTimeout, MAX_SET_TIMEOUT_VALUE))
    }

    componentDidMount() {
        this.props.requestNextChallengeInfo()
        this.setUpdateTimer()
    }

    componentDidUpdate() {
        this.setUpdateTimer()
    }

    openBattleRooms = () => {
        this.props.playButtonClickSound()
        this.props.openBattleRooms()
    }

    openChallenges = () => {
        this.props.playClickSound()
        this.props.openChallenges()
    }

    openJournal = (tab) => {
        if (!this.props.isClanJournalEnabled) return

        this.props.playClickSound()
        this.props.openJournal(tab)
    }

    handleChallengesStatusChange = () => {
        this.props.requestNextChallengeInfo()
    }

    openResourcesJournal = () => {
        this.openJournal(JOURNAL_TABS.resources)
    }

    openShop = () => {
        trackEvent(GA_ACTIONS.TO_STOREFRONT, GA_LABEL.FROM_STRONGHOLD_SCREEN)

        this.props.openShop()
    }

    getChallengesStatusContent() {
        const now = Date.now()
        const {
            start_datetime: start,
            end_datetime: end,
            primetimes_count: primetimesCount,
            primetime_index: primetimeIndex,
        } = this.props.challenge
        const isNotLoaded = start === undefined && end === undefined
        const isDisabled = start === null && end === null
        const isActive = now > start && now < end

        if (isNotLoaded) return ['\xA0', null]
        if (isDisabled) return [CHALLENGES_OFF, CHALLENGES_TOOLTIP_OFF]
        if (isActive) {
            const timer = (
                <Timer
                    endTime={moment(end)}
                    onExpire={this.props.requestNextChallengeInfo}
                    onExpireDelay={1000}
                />
            )
            if (primetimesCount > 1 && primetimeIndex < primetimesCount - 1) {
                return [
                    CHALLENGES_TIMER(<span className={styles.timer}>{timer}</span>),
                    CHALLENGES_TOOLTIP_STAGE_ENDS(timer),
                ]
            }
            return [
                CHALLENGES_TIMER(<span className={styles.timer}>{timer}</span>),
                CHALLENGES_TOOLTIP_ENDS(timer),
            ]
        }

        const isNextStage = primetimesCount > 1 && primetimeIndex > 0
        const momentDate = moment(start)
        if (isTodayDay(momentDate)) return [
            getTodayTime(momentDate),
            isNextStage ?
                CHALLENGES_TOOLTIP_NEXT_STAGE_TODAY(momentDate) :
                CHALLENGES_TOOLTIP_NEXT_TODAY(momentDate),
        ]
        if (isTomorrowDay(momentDate)) return [
            getTomorrowTime(momentDate),
            isNextStage ?
                CHALLENGES_TOOLTIP_NEXT_STAGE_TOMORROW(momentDate) :
                CHALLENGES_TOOLTIP_NEXT_TOMORROW(momentDate),
        ]
        return [
            getCustomDateTime(momentDate),
            isNextStage ?
                CHALLENGES_TOOLTIP_NEXT_STAGE_CUSTOM_DATE(momentDate) :
                CHALLENGES_TOOLTIP_NEXT_CUSTOM_DATE(momentDate),
        ]
    }

    clearTimeouts() {
        if (this.timeoutId) clearTimeout(this.timeoutId)
        if (this.timer) clearTimeout(this.timer)
    }

    render() {
        const activeBattleRooms = this.props.clanUnitsNumber > 0

        const [challengeStatus, challengeTooltipText] = this.getChallengesStatusContent()

        const challengesTabSelected = this.props.location.includes(CHALLENGES)
        const challengesComponent = (
            <div className={styles.cell}>
                <Item
                    selected={challengesTabSelected}
                    title={CHALLENGES_TITLE}
                    contents={challengeStatus}
                    type='challenges'
                    tooltipId={CHALLENGES_TOOLTIP_ID}
                    handleClick={challengesTabSelected ? null : this.openChallenges}
                    handleHover={challengesTabSelected ? null : this.props.playHoverSound}
                />
                <Tooltip id={CHALLENGES_TOOLTIP_ID}>
                    <div className={styles.tooltipContent}>
                        <p className={styles.tooltipSubtitle}>{CHALLENGES_TITLE}</p>
                        <p className={styles.tooltipMessage}>{challengeTooltipText}</p>
                        <p className={styles.tooltipMessage}>{CHALLENGES_TOOLTIP_FOOTER}</p>
                    </div>
                </Tooltip>
            </div>
        )

        const isJournalTabSelected = this.props.location === LOG
        const journalComponent = this.props.isClanJournalEnabled && (
            <div className={styles.cell}>
                <Item
                    selected={isJournalTabSelected}
                    title={JOURNAL_TITLE}
                    type='journal'
                    tooltipId={JOURNAL_TOOLTIP_ID}
                    handleClick={isJournalTabSelected ? null : this.openJournal}
                    handleHover={isJournalTabSelected ? null : this.props.playHoverSound}
                />
                <Tooltip id={JOURNAL_TOOLTIP_ID}>
                    <div className={styles.tooltipContent}>
                        <p className={styles.tooltipSubtitle}>{JOURNAL_TITLE}</p>
                        <p className={styles.tooltipMessage}>{JOURNAL_TOOLTIP}</p>
                    </div>
                </Tooltip>
            </div>
        )

        const onScheduleButtonClick = () => scheduleOpenThrottler.throttle(this.props.openSchedule)
        const scheduleButtonComponent = (
            this.props.isScheduleEnabled &&
            <ScheduleButton
                onClick={onScheduleButtonClick}
                primary={activeBattleRooms}
                showBubble={this.props.showScheduleBubble}
            />
        )

        const battlesComponent = (
            <div className={classNames(styles.button, {
                [styles.button__highlight]: this.props.industrialResourceMultiplier > 1,
            })}
            >
                <BattlesButton
                    handleClick={this.openBattleRooms}
                    handleHover={this.props.playHoverSound}
                    caption={OPEN_BATTLE_LIST_BUTTON_CAPTION}
                    primary={activeBattleRooms}
                    tooltipId={OPEN_BATTLE_LIST_BUTTON_TOOLTIP_ID}
                    industrialResourceMultiplier={this.props.industrialResourceMultiplier}
                />
                {scheduleButtonComponent}
                <Tooltip id={OPEN_BATTLE_LIST_BUTTON_TOOLTIP_ID}>
                    <div className={styles.tooltipContent}>
                        <p className={styles.tooltipSubtitle}>{OPEN_BATTLE_LIST_BUTTON_CAPTION}</p>
                        {activeBattleRooms &&
                        <p className={styles.tooltipMessage}>
                            {OPEN_BATTLE_LIST_BUTTON_TOOLTIP_UNITS_AVAILABLE(this.props.clanUnitsNumber)}
                        </p>
                        }
                        {this.props.industrialResourceMultiplier > 1 &&
                        <p className={styles.tooltipMessage}>
                            {INDUSTRIAL_RESOURCE_MULTIPLIER_TOOLTIP_TEXT(this.props.industrialResourceMultiplier)}
                        </p>
                        }
                        <p className={styles.tooltipMessage}>{OPEN_BATTLE_LIST_BUTTON_TOOLTIP_MESSAGE}</p>
                    </div>
                </Tooltip>
            </div>
        )

        const storageIsOpen = (
            (this.props.location === LOG) && (this.props.journalSelectedTab === JOURNAL_TABS.resources)
        )

        const storage = this.props.clanId && (
            <div className={styles.cell}>
                <Storage
                    selected={storageIsOpen}
                    industrialResource={this.props.clanIndustrialResource}
                    handleClick={this.openResourcesJournal}
                />
            </div>
        )

        const industrialResourceBuyinButton = this.props.industrialResourceBuyinAvailable && (
            <div className={classNames(styles.cell, styles.industrialResourceBuyinButton)}>
                <Button
                    type="secondary"
                    onClick={this.openShop}
                    tooltipId={IndustrialBuyinTooltip.id}
                >
                    <span className={styles.industrialResourceBuyinText}>
                        {INDUSTRIAL_RESOURCE_BUYIN_BUTTON}
                    </span>
                </Button>
                <IndustrialBuyinTooltip/>
            </div>
        )

        const className = classNames(styles.base, {
            [styles.base__active]: activeBattleRooms,
        })

        const leftPartClassName = classNames(styles.part, styles.part__left)
        const rightPartClassName = classNames(styles.part, styles.part__right)
        const centralPartClassName = classNames(styles.part, styles.part__center)
        return (
            <div className={className}>
                <div className={styles.background}/>
                <div className={leftPartClassName}>
                    {challengesComponent}
                    {journalComponent}
                </div>
                <div className={centralPartClassName}>
                    {battlesComponent}
                </div>
                <div className={rightPartClassName}>
                    {storage}
                    {industrialResourceBuyinButton}
                </div>
            </div>
        )
    }
}


export const mapStateToProps = state => {
    const {
        account,
        challenges: { nextChallenge: challenge },
        clan,
        userFlags,
        journal: { selectedTab: journalSelectedTab },
        routing,
        settings: { industrialResourceMultiplier },
    } = state

    const location = routing.locationBeforeTransitions.pathname

    const isScheduleEnabled = isFeatureAvailable(account, FEATURE_TYPES.SCHEDULE)
    const isClanJournalEnabled = clan.id !== null && isFeatureAvailable(account, FEATURE_TYPES.CLAN_JOURNAL)

    const showClanTreasuryTabs = account.permissions.see_treasury || false

    const showScheduleBubble = !userFlags.schedule_visited

    return {
        industrialResourceBuyinAvailable: !!state.shop.status && state.shop.status.enabled,
        challenge,
        clanId: clan.id,
        clanIndustrialResource: clan.industrial_resource,
        clanTreasury: clan.treasury,
        clanUnitsNumber: clan.units_number,
        isClanJournalEnabled,
        isScheduleEnabled,
        journalSelectedTab,
        location,
        showClanTreasuryTabs,
        showScheduleBubble,
        industrialResourceMultiplier,
    }
}

export const mapDispatchToProps = {
    openBattleRooms,
    openChallenges,
    openJournal,
    openSchedule,
    openShop: () => push(SHOP_STOREFRONT),

    requestNextChallengeInfo,

    playHoverSound,
    playButtonClickSound,
    playClickSound,
}

export default connect(mapStateToProps, mapDispatchToProps)(ClanPanel)



// WEBPACK FOOTER //
// ./containers/ClanPanel/ClanPanel.jsx